// src/components/TreasureModal.jsx
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Modal for entering keyword to unlock a treasure
 * @param {Object} treasure - Current treasure { id, lat, lng, keyword, found }
 * @param {Function} onSubmit - Callback with keyword submission
 * @param {Function} onClose - Callback to close modal
 */
export default function TreasureModal({ treasure, onSubmit, onClose }) {
  const { t } = useTranslation()
  const [keyword, setKeyword] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  function handleSubmit(e) {
    e.preventDefault()

    if (!keyword.trim()) {
      setError(t('treasure.enterKeyword'))
      return
    }

    setSubmitting(true)
    setError(null)

    // Simulate verification delay
    setTimeout(() => {
      const isCorrect = keyword.toLowerCase() === treasure.keyword.toLowerCase()

      if (isCorrect) {
        setKeyword('')
        setSubmitting(false)
        onSubmit(treasure.id, keyword)
        onClose()
      } else {
        setError(t('treasure.notCorrect'))
        setSubmitting(false)
      }
    }, 500)
  }

  if (!treasure) return null

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{t('treasure.found')}</h2>

        <p style={{ color: '#666', marginBottom: '20px' }}>
          {t('treasure.enterKeyword')}
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder={t('treasure.keyword')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            disabled={submitting}
            autoFocus
            style={styles.input}
          />

          {error && (
            <p style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                ...styles.button,
                background: '#6c757d',
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {t('treasure.cancel')}
            </button>

            <button
              type="submit"
              disabled={submitting}
              style={{
                ...styles.button,
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? t('admin.checking') : t('treasure.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    padding: '30px',
    borderRadius: '8px',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  button: {
    flex: 1,
    padding: '12px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '4px',
    background: '#007bff',
    color: 'white',
    cursor: 'pointer',
    transition: 'background 0.2s'
  }
}
